/**
 * Dnd5eRules — Rule provider for D&D 5e.
 * Pure functions, no side effects, no I/O.
 * Delegates to existing helpers where possible.
 *
 * NOTE: Types HudCharacterData, AbilityStat, SkillId, SKILL_TO_STAT etc.
 * live in src/lib/character-hud-store.ts (frontend). We re-define compatible
 * type aliases here so shared/ doesn't depend on src/.
 * The adapter (hud-adapter.ts) bridges both worlds.
 *
 * STRUCTURAL CONTRACT: RulesCharacterData is intentionally a structural
 * supertype — HudCharacterData (frontend) must be directly assignable to it
 * without casts. When tightening fields here, also verify the frontend type
 * still satisfies the contract (see src/lib/character-hud-store.ts).
 */

import { abilityMod } from '../pointBuy.js';
import {
  calculateAC,
  parseInventory,
  findEquippedArmor,
  hasEquippedShield,
} from '../EquipmentAutomation.js';
import type { ClassFeatures } from '../EquipmentAutomation.js';
import { calculateCasterLevel, MULTICLASS_SPELL_SLOTS } from '../multiclass-spells.js';
import { srdClasses } from '../srd/classes.js';
import type { SrdEquipmentItem, SrdClassFeature, SrdClass } from '../srd/types.js';
import type { ClassEntry } from '../types.js';

// ─── Type aliases (structurally compatible with src/lib/character-hud-store) ──

export type AbilityStat = 'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha';

export type SkillId =
  | 'acrobatics' | 'animalHandling' | 'arcana' | 'athletics'
  | 'deception' | 'history' | 'insight' | 'intimidation'
  | 'investigation' | 'medicine' | 'nature' | 'perception'
  | 'performance' | 'persuasion' | 'religion' | 'sleightOfHand'
  | 'stealth' | 'survival';

export const SKILL_TO_STAT: Record<SkillId, AbilityStat> = {
  acrobatics: 'dex', animalHandling: 'wis', arcana: 'int', athletics: 'str',
  deception: 'cha', history: 'int', insight: 'wis', intimidation: 'cha',
  investigation: 'int', medicine: 'wis', nature: 'int', perception: 'wis',
  performance: 'cha', persuasion: 'cha', religion: 'int', sleightOfHand: 'dex',
  stealth: 'dex', survival: 'wis',
};

/**
 * Minimal subset of HudCharacterData that rules functions require.
 * Structurally compatible with the full HudCharacterData from src/lib/character-hud-store.ts.
 *
 * Field-shape choices below are deliberately permissive so that the frontend
 * HudCharacterData (which may carry richer/narrower types per field) is
 * directly assignable to this type — no `as unknown as` casts required.
 */
export interface RulesCharacterData {
  identity: {
    level: number;
    name?: string;
    race?: string;
    className?: string;
    alignment?: string;
    background?: string;
    portrait?: string;
  };
  stats: Record<AbilityStat, number>;
  saves: Record<AbilityStat, 0 | 1>;
  skills: Record<SkillId, 0 | 1 | 2>;
  hp: { current: number; max: number; temp: number };
  ac: number;
  speed: number;
  spells: {
    stat: AbilityStat;
    slots: Record<number, { max: number; current: number }>;
    known: Array<{ id: string; spellSlug?: string; name: string; level: number; state: string; school?: string; components?: { v?: boolean; s?: boolean; m?: boolean; materialDesc?: string }; ritual?: boolean; concentration?: boolean; castingTime?: string; range?: string; duration?: string; description?: string; higherLevels?: string; actionType?: 'action' | 'bonus' | 'reaction' | 'minute' | 'hour' | 'special'; roll?: { attack?: 'ranged' | 'melee'; saveStat?: AbilityStat; saveHalfOnSuccess?: boolean } }>;
    concentrationSpellId: string | null;
  };
  combat: {
    initiative: number;
    /** Wave 6 — user-editable bonus piece of hybrid initiative formula. */
    initiativeBonus?: number;
    attacks: Array<{ id: string; name: string; attackBonus: number; damage: string; damageType: string; meta?: string }>;
    round: number;
    actionEconomy: { action: boolean; bonus: boolean; reaction: boolean };
    /** Feet of movement spent this turn. Persisted to values.movement_spent;
     * the server's ActionEconomyResetSubscriber zeroes it on initiative:next. */
    movementSpent?: number;
  };
  deathSaves: { successes: number; failures: number };
  features: Array<{ id: string; name: string; source: string; description: string; uses?: { current: number; max: number; recharge: string } }>;
  inventory: Array<{ id: string; name: string; quantity: number; weight?: number; equipped?: boolean; description?: string; weapon?: boolean; item_type?: 'weapon' | 'armor' | 'shield' | 'gear'; srd_slug?: string; base_ac?: number; armor_category?: 'Light' | 'Medium' | 'Heavy'; attuned?: boolean }>;
  currency: { cp: number; sp: number; ep: number; gp: number; pp: number };
  classData: Array<{ className: string; subclass?: string; level: number; hitDie: string }>;
  hitDice: { total: number; used: number; dieType: string };
  activeEffects: Array<{ id: string; name: string; source: string; duration?: string; description?: string }>;
  proficiencies: string[];
  resources: Array<{ id: string; name: string; current: number; max: number; recharge: string }>;
  rp: {
    ideal: string;
    bond: string;
    flaw: string;
    backstory: string;
    manners: string;
    goals: Array<{ id: string; text: string; subtitle?: string; done: boolean; priority: string }>;
    npcs: Array<{ id: string; name: string; relation: string; notes?: string }>;
    factions: Array<{ id: string; name: string; loyalty: number; notes?: string }>;
    sessionLog: Array<{ id: string; date: string; summary: string; title?: string; number?: number }>;
    moments: Array<{ id: string; title: string; description: string; session?: string; type?: string }>;
    /**
     * Board layout cards — opaque to rules layer. Use `unknown[]` so any
     * concrete `HudCardConfig`-shaped element (which may lack an index
     * signature) is assignable without an explicit cast.
     */
    boardConfig: { cards: unknown[]; autoLayout: boolean };
  };
  inspiration: boolean;
  xp?: number;
  /** Combat scratch notes — F-005/BF-008 round-trip support */
  combat_notes?: string;
  /** Wave 6 — user-editable bonus to passive perception. */
  passivePerceptionBonus?: number;
}

/** Feature type from HudCharacterData (structural alias) */
export interface RulesFeature {
  id: string;
  name: string;
  source: string;
  description: string;
  uses?: { current: number; max: number; recharge: string };
}

// ─── XP Thresholds (moved from IdentitySection.tsx) ─────────────

export const XP_THRESHOLDS: Record<number, number> = {
  1: 0, 2: 300, 3: 900, 4: 2700, 5: 6500,
  6: 14000, 7: 23000, 8: 34000, 9: 48000, 10: 64000,
  11: 85000, 12: 100000, 13: 120000, 14: 140000, 15: 165000,
  16: 195000, 17: 225000, 18: 265000, 19: 305000, 20: 355000,
};

// ─── Feature Categorization (moved from AbilitiesSection.tsx) ────

export type FeatureCategory = 'class' | 'race' | 'feats';

export function categorizeFeature(feature: RulesFeature): FeatureCategory {
  const source = feature.source.toLowerCase();
  if (
    source.includes('раса') || source.includes('полуэльф') ||
    source.includes('эльф') || source.includes('дварф') ||
    source.includes('тифлинг') || source.includes('драконорожд')
  ) {
    return 'race';
  }
  if (source.includes('фит') || source.includes('feat')) {
    return 'feats';
  }
  return 'class';
}

// ─── AC Breakdown ────────────────────────────────────────────────

export interface BreakdownRow {
  label: string;
  value: number;
  type: 'base' | 'dex' | 'shield' | 'misc' | 'feature';
}

export interface AcBreakdownResult {
  base: number;
  dexMod: number;
  armorBonus: number;
  shieldBonus: number;
  miscBonus: number;
  total: number;
  rows: BreakdownRow[];
}

// ─── Dnd5eRules Interface ────────────────────────────────────────

export interface Dnd5eRules {
  abilityMod(score: number): number;
  pb(level: number): number;
  passive(modifier: number): number;
  fmt(n: number): string;
  activeSkillMod(data: RulesCharacterData, skill: SkillId): number;
  activeSaveMod(data: RulesCharacterData, stat: AbilityStat): number;
  attackBonus(data: RulesCharacterData, stat: AbilityStat): number;
  spellDC(data: RulesCharacterData): number;
  spellAttackBonus(data: RulesCharacterData): number;
  acBreakdown(data: RulesCharacterData, equipmentMap?: Map<string, SrdEquipmentItem>): AcBreakdownResult;
  xpToNextLevel(level: number): number;
  carryingCapacity(str: number): number;
  featuresAtLevel(classSlug: string, level: number): SrdClassFeature[];
  spellSlotsAtLevel(classData: ClassEntry[]): Record<number, number>;
  restRecover(data: RulesCharacterData, restType: 'short' | 'long'): Partial<RulesCharacterData>;
}

// ─── Implementation ──────────────────────────────────────────────

function pb(level: number): number {
  return Math.ceil(level / 4) + 1;
}

function passive(modifier: number): number {
  return 10 + modifier;
}

function fmt(n: number): string {
  return n >= 0 ? `+${n}` : `\u2212${Math.abs(n)}`;
}

function activeSkillMod(data: RulesCharacterData, skill: SkillId): number {
  const stat = SKILL_TO_STAT[skill];
  const statMod = abilityMod(data.stats[stat]);
  const profLevel = data.skills[skill]; // 0 | 1 | 2
  return statMod + profLevel * pb(data.identity.level);
}

function activeSaveMod(data: RulesCharacterData, stat: AbilityStat): number {
  const statMod = abilityMod(data.stats[stat]);
  const proficient = data.saves[stat];
  return statMod + proficient * pb(data.identity.level);
}

function attackBonusFn(data: RulesCharacterData, stat: AbilityStat): number {
  return abilityMod(data.stats[stat]) + pb(data.identity.level);
}

function spellDC(data: RulesCharacterData): number {
  return 8 + pb(data.identity.level) + abilityMod(data.stats[data.spells.stat]);
}

function spellAttackBonus(data: RulesCharacterData): number {
  return pb(data.identity.level) + abilityMod(data.stats[data.spells.stat]);
}

function acBreakdown(
  data: RulesCharacterData,
  equipmentMap?: Map<string, SrdEquipmentItem>,
): AcBreakdownResult {
  const dexMod = abilityMod(data.stats.dex);
  const rows: BreakdownRow[] = [];

  // If no equipment map, return simple fallback using data.ac
  if (!equipmentMap) {
    rows.push({ label: 'Базовый КЗ', value: data.ac, type: 'base' });
    return {
      base: data.ac,
      dexMod: 0,
      armorBonus: 0,
      shieldBonus: 0,
      miscBonus: 0,
      total: data.ac,
      rows,
    };
  }

  // Parse inventory to find armor
  const inventoryRows = data.inventory.map((item, i) => ({
    rowId: item.id ?? `inv-${i}`,
    values: {
      name: item.name,
      srd_slug: item.id,
      item_type: item.equipped ? (item.weight && item.weight >= 10 ? 'armor' : 'weapon') : 'gear',
      equipped: item.equipped ?? false,
    } as Record<string, string | number | boolean | string[]>,
  }));

  const inventory = parseInventory(inventoryRows);
  const armor = findEquippedArmor(inventory, equipmentMap);
  const hasShield = hasEquippedShield(inventory, equipmentMap);

  // Detect unarmored defense features
  const classFeatures: ClassFeatures = {};
  const classNames = data.classData.map(c => c.className.toLowerCase());
  if (classNames.some(n => n.includes('barbarian') || n.includes('варвар'))) {
    classFeatures.unarmoredDefense = 'barbarian';
  } else if (classNames.some(n => n.includes('monk') || n.includes('монах'))) {
    classFeatures.unarmoredDefense = 'monk';
  }

  const conMod = abilityMod(data.stats.con);
  const wisMod = abilityMod(data.stats.wis);
  const total = calculateAC(armor, hasShield, dexMod, classFeatures, conMod, wisMod);

  // Build breakdown rows
  if (armor) {
    const category = armor.armorCategory ?? '';
    const base = armor.acBase ?? 10;
    rows.push({ label: armor.name, value: base, type: 'base' });

    if (category === 'Heavy') {
      // No DEX for heavy
    } else if (category === 'Medium') {
      const cap = armor.acMaxBonus ?? 2;
      rows.push({ label: 'Модификатор ЛОВ', value: Math.min(dexMod, cap), type: 'dex' });
    } else {
      rows.push({ label: 'Модификатор ЛОВ', value: dexMod, type: 'dex' });
    }

    if (hasShield) {
      rows.push({ label: 'Щит', value: 2, type: 'shield' });
    }

    return {
      base,
      dexMod: category === 'Heavy' ? 0 : category === 'Medium' ? Math.min(dexMod, armor.acMaxBonus ?? 2) : dexMod,
      armorBonus: base - 10,
      shieldBonus: hasShield ? 2 : 0,
      miscBonus: 0,
      total,
      rows,
    };
  }

  // No armor
  rows.push({ label: 'Базовый', value: 10, type: 'base' });
  rows.push({ label: 'Модификатор ЛОВ', value: dexMod, type: 'dex' });

  if (classFeatures.unarmoredDefense === 'barbarian') {
    rows.push({ label: 'Защита без доспехов (ВЫН)', value: conMod, type: 'feature' });
  } else if (classFeatures.unarmoredDefense === 'monk') {
    rows.push({ label: 'Защита без доспехов (МДР)', value: wisMod, type: 'feature' });
  }

  if (hasShield) {
    rows.push({ label: 'Щит', value: 2, type: 'shield' });
  }

  return {
    base: 10,
    dexMod,
    armorBonus: 0,
    shieldBonus: hasShield ? 2 : 0,
    miscBonus: classFeatures.unarmoredDefense === 'barbarian' ? conMod
      : classFeatures.unarmoredDefense === 'monk' ? wisMod : 0,
    total,
    rows,
  };
}

function xpToNextLevel(level: number): number {
  return XP_THRESHOLDS[level + 1] ?? XP_THRESHOLDS[20] ?? 355000;
}

function carryingCapacity(str: number): number {
  return str * 15;
}

function featuresAtLevel(classSlug: string, level: number): SrdClassFeature[] {
  const srdClass: SrdClass | undefined = srdClasses.find(c => c.slug === classSlug);
  if (!srdClass) return [];
  return srdClass.features.filter(f => f.level <= level);
}

function spellSlotsAtLevel(classData: ClassEntry[]): Record<number, number> {
  if (classData.length === 0) return {};
  const { casterLevel } = calculateCasterLevel(classData);
  if (casterLevel === 0) return {};
  return MULTICLASS_SPELL_SLOTS[casterLevel] ?? {};
}

function restRecover(
  data: RulesCharacterData,
  restType: 'short' | 'long',
): Partial<RulesCharacterData> {
  const result: Partial<RulesCharacterData> = {};

  if (restType === 'long') {
    // Long rest: full HP recovery
    result.hp = { current: data.hp.max, max: data.hp.max, temp: 0 };

    // Recover all spell slots to max
    const slots: Record<number, { max: number; current: number }> = {};
    for (const [lvl, slot] of Object.entries(data.spells.slots)) {
      slots[Number(lvl)] = { max: slot.max, current: slot.max };
    }
    result.spells = { ...data.spells, slots };

    // Recover half of total hit dice (minimum 1)
    const recoverDice = Math.max(1, Math.floor(data.hitDice.total / 2));
    const newUsed = Math.max(0, data.hitDice.used - recoverDice);
    result.hitDice = { ...data.hitDice, used: newUsed };

    // Reset death saves
    result.deathSaves = { successes: 0, failures: 0 };

    // Recover features with long rest recharge
    result.features = data.features.map(f => {
      if (f.uses && (f.uses.recharge === 'long' || f.uses.recharge === 'short')) {
        return { ...f, uses: { ...f.uses, current: f.uses.max } };
      }
      return f;
    });
  } else {
    // Short rest: recover features with short rest recharge
    result.features = data.features.map(f => {
      if (f.uses && f.uses.recharge === 'short') {
        return { ...f, uses: { ...f.uses, current: f.uses.max } };
      }
      return f;
    });
  }

  return result;
}

// ─── Singleton ───────────────────────────────────────────────────

export const dnd5eRules: Dnd5eRules = {
  abilityMod,
  pb,
  passive,
  fmt,
  activeSkillMod,
  activeSaveMod,
  attackBonus: attackBonusFn,
  spellDC,
  spellAttackBonus,
  acBreakdown,
  xpToNextLevel,
  carryingCapacity,
  featuresAtLevel,
  spellSlotsAtLevel,
  restRecover,
};
